import type React from 'react';
import { useEffect, useState } from 'react';
import ExpanderPanel from '@rio-cloud/rio-uikit/ExpanderPanel';
import { getTrackingAttributes } from '@rio-cloud/rio-uikit/googleAnalyticsUtils';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { FormattedMessage } from 'react-intl';
import { FormInputField } from './FormInputField';
import { useLocation, useNavigate } from 'react-router';
import { TagListDetailsFooter } from './sidebarfooter/TagListDetailsFooter';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import { config } from '../../../../config';
import {
    getAssignedAssets,
    getAssignedAssetsLoaded,
    getAssignedDrivers,
    getAssignedDriversLoaded,
    getSelectedTag,
    getSelectedTagVersion,
    getUnsavedChanges,
} from '../Tags.selector';
import { getUserAccount } from '../../../../configuration/login/loginSlice';
import { isHierarchyEnabled } from '../hierarchy/hierarchy';
import { setUnsavedChanges } from '../tagsSlice';

export interface TagListDetailsProps {
    toggleDiscardChangesDialog: (opened: boolean, rowId: string | null) => void;
}

const SIDEBAR_BREAKPOINT = 1000;
export const ERROR_CODE_NAME_ALREADY_IN_USE = 'T06';
export const ERROR_CODE_GENERIC = 'TXX';
const MAX_VISIBLE_EXPANDER_ROWS = 5;
const LINK_TRACKING_CATEGORY = 'link autoTracking';
const LINK_TRACKING_ACTION = 'link click';

const getLocalUrl = (port: number) => `http://${window.location.hostname}:${port}`;
const resolveLink = (liveUrl: string | undefined, localPort: number) =>
    import.meta.env.DEV ? getLocalUrl(localPort) : (liveUrl ?? '');

const buildFilteredLink = (baseUrl: string, route: 'assets' | 'drivers', queryKey: 'q', filterValue: string) =>
    `${baseUrl.replace(/\/$/, '')}/#/${route}?${queryKey}=${encodeURIComponent(filterValue)}`;

const buildLinkTrackingAttributes = (label: string) =>
    getTrackingAttributes({
        trigger: 'click',
        category: LINK_TRACKING_CATEGORY,
        action: LINK_TRACKING_ACTION,
        label,
    });

export const TagListDetails = (props: TagListDetailsProps) => {
    const { toggleDiscardChangesDialog } = props;

    const selectedTag = useAppSelector(getSelectedTag);
    const hierarchy = isHierarchyEnabled(useAppSelector(getUserAccount));
    const assignedAssets = useAppSelector(getAssignedAssets);
    const assignedAssetsLoaded = useAppSelector(getAssignedAssetsLoaded);
    const assignedDrivers = useAppSelector(getAssignedDrivers);
    const assignedDriversLoaded = useAppSelector(getAssignedDriversLoaded);
    const unsavedChanges = useAppSelector(getUnsavedChanges);
    const selectedTagVersion = useAppSelector(getSelectedTagVersion);

    const dispatch = useAppDispatch();

    const initialTagName = selectedTag?.name ?? '';
    const [currentTagName, setCurrentTagName] = useState<string>(initialTagName);
    const [assetsExpanderOpen, setAssetsExpanderOpen] = useState(true);
    const [driversExpanderOpen, setDriversExpanderOpen] = useState(true);
    const location = useLocation();
    const navigate = useNavigate();

    const selectedTagId = selectedTag?.id;
    const selectedTagAssets = selectedTagId ? (assignedAssets[selectedTagId] ?? []) : [];
    const selectedTagDrivers = selectedTagId ? (assignedDrivers[selectedTagId] ?? []) : [];
    const selectedTagAssetsLink = currentTagName
        ? buildFilteredLink(resolveLink(config.links.assetAdministration, 3010), 'assets', 'q', currentTagName)
        : '';
    const selectedTagDriversLink = currentTagName
        ? buildFilteredLink(resolveLink(config.links.driverAdministration, 3011), 'drivers', 'q', currentTagName)
        : '';
    const assetAdministrationLink = 'https://administration.assets.rio.cloud/#/assets';
    const driverAdministrationLink = 'https://drivers.rio.cloud/#/drivers';

    useEffect(() => {
        const tagName = selectedTag?.name ?? '';
        setCurrentTagName(tagName);
    }, [selectedTag]);

    useEffect(() => {
        setAssetsExpanderOpen(true);
        setDriversExpanderOpen(true);
    }, [selectedTagId]);

    const onClose = () => {
        if (unsavedChanges) {
            toggleDiscardChangesDialog(true, null);
        } else {
            navigate({ pathname: '/tags', search: location.search });
        }
    };

    const onTagNameValueChange = (e: React.FormEvent<HTMLInputElement>) => {
        const newName = e.currentTarget.value;
        dispatch(setUnsavedChanges(newName !== initialTagName));
        setCurrentTagName(newName);
    };

    const renderExpandableList = <T extends { id: string; status: string }>(
        items: T[],
        renderRow: (item: T) => React.ReactNode,
        emptyMessage: React.ReactNode,
        moreLinkHref: string
    ) => {
        if (items.length === 0) {
            return <div className='padding-15 text-color-dark'>{emptyMessage}</div>;
        }

        const visibleItems = items.slice(0, MAX_VISIBLE_EXPANDER_ROWS);
        const hiddenItemCount = items.length - visibleItems.length;

        return (
            <div className='list-group rounded-bottom border border-top-only margin-0'>
                {visibleItems.map(renderRow)}
                {hiddenItemCount > 0 && (
                    <div className='list-group-item'>
                        {moreLinkHref ? (
                            <a
                                href={moreLinkHref}
                                target='_blank'
                                rel='noreferrer'
                                {...buildLinkTrackingAttributes('tags.tagDetails.moreRows')}
                            >
                                <FormattedMessage
                                    id='tags.tagDetails.moreRows'
                                    defaultMessage='+{count} weitere'
                                    values={{ count: hiddenItemCount }}
                                />
                            </a>
                        ) : (
                            <div className='text-color-dark'>
                                <FormattedMessage
                                    id='tags.tagDetails.moreRows'
                                    defaultMessage='+{count} weitere'
                                    values={{ count: hiddenItemCount }}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const sideBarContent = () => (
        <div className='display-flex flex-column gap-10'>
            <form data-cy='detail-view-form'>
                <FormInputField
                    translationId={'tags.tagDetails.name'}
                    currentValue={currentTagName}
                    disabled={hierarchy || selectedTagVersion === null}
                    onValueChange={onTagNameValueChange}
                    dataAttribute='detail-view-form-name'
                    withCopyButton={true}
                    maxLength={100}
                />
            </form>
            {hierarchy && (
                <p>Resource assignments shown here are direct assignments. Child groups do not inherit them.</p>
            )}
            <ExpanderPanel
                title={<FormattedMessage id='tags.tagDetails.assets.title' defaultMessage='Assets' />}
                bsStyle='default'
                className='shadow-subtle'
                bodyClassName='padding-0'
                open={assetsExpanderOpen}
                onToggle={setAssetsExpanderOpen}
            >
                {assignedAssetsLoaded ? (
                    renderExpandableList(
                        selectedTagAssets,
                        asset => (
                            <div key={asset.id} className='list-group-item'>
                                <div className='display-flex align-items-start gap-10'>
                                    <span className='uikit-icon icon-truck margin-top-3' aria-hidden='true' />
                                    <div className='flex-1-1'>
                                        <div className='text-medium'>{asset.name}</div>
                                        <div className='text-color-dark'>
                                            {asset.identification ? (
                                                <FormattedMessage
                                                    id='tags.tagDetails.assets.identification'
                                                    defaultMessage='VIN {identification}'
                                                    values={{ identification: asset.identification }}
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    id='tags.tagDetails.assets.noIdentification'
                                                    defaultMessage='No identification'
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <span className='label label-condensed label-success'>{asset.status}</span>
                                </div>
                            </div>
                        ),
                        <a
                            href={assetAdministrationLink}
                            target='_blank'
                            rel='noreferrer'
                            {...buildLinkTrackingAttributes('tags.tagDetails.assets.noAssetsAssigned')}
                        >
                            No assets assigned
                        </a>,
                        selectedTagAssetsLink
                    )
                ) : (
                    <div className='padding-15 text-color-dark'>
                        <FormattedMessage id='tags.tagDetails.assets.loading' defaultMessage='Loading assets...' />
                    </div>
                )}
            </ExpanderPanel>
            <ExpanderPanel
                title={<FormattedMessage id='tags.tagDetails.drivers.title' defaultMessage='Drivers' />}
                bsStyle='default'
                className='shadow-subtle'
                bodyClassName='padding-0'
                open={driversExpanderOpen}
                onToggle={setDriversExpanderOpen}
            >
                {assignedDriversLoaded ? (
                    renderExpandableList(
                        selectedTagDrivers,
                        driver => (
                            <div key={driver.id} className='list-group-item'>
                                <div className='display-flex align-items-start gap-10'>
                                    <span className='uikit-icon icon-driver margin-top-3' aria-hidden='true' />
                                    <div className='flex-1-1'>
                                        <div className='text-medium'>{driver.displayName}</div>
                                    </div>
                                    <span className='label label-condensed label-success'>{driver.status}</span>
                                </div>
                            </div>
                        ),
                        <a
                            href={driverAdministrationLink}
                            target='_blank'
                            rel='noreferrer'
                            {...buildLinkTrackingAttributes('tags.tagDetails.drivers.noDriversAssigned')}
                        >
                            No drivers assigned
                        </a>,
                        selectedTagDriversLink
                    )
                ) : (
                    <div className='padding-15 text-color-dark'>
                        <FormattedMessage id='tags.tagDetails.drivers.loading' defaultMessage='Loading drivers...' />
                    </div>
                )}
            </ExpanderPanel>
        </div>
    );

    return (
        <Sidebar
            fly={
                // TODO workaround for infinite update loop with uikit table component
                import.meta.env.MODE === 'test'
            }
            title={currentTagName}
            closed={false}
            position={'right'}
            onClose={onClose}
            resizable={false}
            minWidth={300}
            width={420}
            footer={<TagListDetailsFooter newTagName={currentTagName} />}
            switchModeBreakpoint={SIDEBAR_BREAKPOINT}
            titleClassName={'padding-left-10'}
            bodyClassName={'padding-left-20 padding-right-20'}
        >
            {sideBarContent()}
        </Sidebar>
    );
};
